import { useState, FormEvent, ChangeEvent } from 'react';

interface HeroSectionProps {
  onScrollTo: (id: string) => void;
}

const FORM_URL = 'https://readdy.ai/api/form/d72l8vaaegmdqoca80n0';

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 3) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3)}`;
  if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
}

export default function HeroSection({ onScrollTo: _onScrollTo }: HeroSectionProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [ageError, setAgeError] = useState('');

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPhone(maskPhone(e.target.value));
  };

  const handleAgeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 2);
    setAge(onlyDigits);
    if (onlyDigits.length > 0) {
      const num = parseInt(onlyDigits, 10);
      setAgeError(num < 3 || num > 35 ? 'A seletiva é para pessoas de 3 a 35 anos.' : '');
    } else {
      setAgeError('');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const ageNum = parseInt(age, 10);
    if (!age || ageNum < 3 || ageNum > 35) {
      setAgeError('A seletiva é para pessoas de 3 a 35 anos.');
      return;
    }
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.set('nome', (form.elements.namedItem('nome') as HTMLInputElement).value);
    data.set('whatsapp', phone);
    data.set('idade', age);
    data.set('cidade', (form.elements.namedItem('cidade') as HTMLInputElement).value);

    setStatus('loading');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        window.location.href = 'https://confirmar.fourmodelsseletivas.com.br/';
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          alt="Fashion Background"
          className="w-full h-full object-cover object-top"
          src="https://public.readdy.ai/ai/img_res/5233cb1ed4f3a8734f8613442c870aa5.jpg"
          loading="eager"
          fetchPriority="high"
          decoding="sync"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-24 sm:pt-28 pb-10 sm:pb-16">
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-14 xl:gap-20">

          {/* ── LEFT: headline + badges + stats ── */}
          <div className="flex-1 text-center lg:text-left">

            {/* Badges */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-5 sm:mb-6">
              <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-calendar-event-fill text-sm"></i>
                19/04 &bull; 14h ou 16h
              </span>
              <span className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-map-pin-fill text-yellow-500 text-sm"></i>
                CAMPO GRANDE - MS
              </span>
              <span className="bg-red-600 text-white px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold tracking-wider whitespace-nowrap animate-pulse flex items-center gap-1.5">
                <i className="ri-fire-fill text-sm"></i>
                VAGAS LIMITADAS
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-black text-white mb-3 sm:mb-4 leading-tight">
              Sua Carreira na Moda, TV e<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                Redes Sociais Começa Aqui
              </span>
            </h1>

            <p className="text-base sm:text-lg text-gray-200 mb-2 font-light max-w-xl mx-auto lg:mx-0">
              Seletiva Oficial <strong className="text-white font-semibold">Four Models</strong> em{' '}
              <strong className="text-yellow-500 font-bold">Campo Grande - MS</strong>
            </p>
            <p className="text-base sm:text-lg text-white font-semibold max-w-xl mx-auto lg:mx-0 mb-6 sm:mb-8 leading-relaxed">
              Descubra se você ou seu filho(a) tem perfil para moda, TV e internet.
            </p>

            {/* Stats */}
            <div className="flex items-center justify-center lg:justify-start gap-0 text-white">
              <div className="text-center pr-6 sm:pr-8 border-r border-gray-600">
                <div className="text-2xl sm:text-3xl font-black text-yellow-500">03&ndash;35</div>
                <div className="text-xs text-gray-400 mt-0.5">Anos</div>
              </div>
              <div className="text-center px-6 sm:px-8 border-r border-gray-600">
                <div className="text-2xl sm:text-3xl font-black text-yellow-500">100%</div>
                <div className="text-xs text-gray-400 mt-0.5">Gratuito</div>
              </div>
              <div className="text-center pl-6 sm:pl-8">
                <div className="text-2xl sm:text-3xl font-black text-yellow-500">0</div>
                <div className="text-xs text-gray-400 mt-0.5">Exp. Necessária</div>
              </div>
            </div>
          </div>

          {/* ── RIGHT: form card ── */}
          <div className="w-full lg:w-[420px] xl:w-[440px] flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
              {/* Card header */}
              <div className="text-center mb-5">
                <div className="inline-flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-3 py-1 mb-3">
                  <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full inline-block animate-pulse"></span>
                  <span className="text-yellow-700 text-xs font-bold">Inscrições abertas</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-black leading-tight">
                  Garanta sua{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">
                    vaga gratuita
                  </span>
                </h2>
                <p className="text-gray-500 text-xs mt-1">Leva menos de 30 segundos.</p>
              </div>

              <form
                id="form-hero"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="space-y-3"
              >
                {/* Nome */}
                <div>
                  <input
                    required
                    name="nome"
                    autoComplete="name"
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-yellow-500 text-sm text-gray-900 placeholder-gray-400 transition-colors"
                    placeholder="Nome completo *"
                  />
                </div>

                {/* WhatsApp */}
                <div>
                  <input
                    required
                    name="whatsapp"
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={handlePhoneChange}
                    autoComplete="tel"
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-yellow-500 text-sm text-gray-900 placeholder-gray-400 transition-colors"
                    placeholder="WhatsApp *"
                  />
                </div>

                {/* Idade + Cidade side by side */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      required
                      name="idade"
                      inputMode="numeric"
                      value={age}
                      onChange={handleAgeChange}
                      maxLength={2}
                      className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none text-sm text-gray-900 placeholder-gray-400 transition-colors ${ageError ? 'border-red-400' : 'border-gray-200 focus:border-yellow-500'}`}
                      placeholder="Idade *"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      required
                      name="cidade"
                      autoComplete="address-level2"
                      className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-yellow-500 text-sm text-gray-900 placeholder-gray-400 transition-colors"
                      placeholder="Cidade *"
                    />
                  </div>
                </div>

                {ageError && (
                  <p className="text-red-500 text-xs font-semibold -mt-1">{ageError}</p>
                )}

                {status === 'error' && (
                  <p className="text-red-500 text-xs text-center">Erro ao enviar. Tente novamente.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading' || !!ageError}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-black py-4 rounded-xl text-sm font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === 'loading'
                    ? <><i className="ri-loader-4-line animate-spin"></i>Enviando...</>
                    : 'GARANTIR MINHA VAGA GRATUITA'}
                </button>
              </form>

              <div className="flex items-center justify-center gap-3 mt-4 text-gray-400">
                <span className="flex items-center gap-1 text-xs">
                  <i className="ri-lock-fill text-green-500 text-sm"></i>100% seguro
                </span>
                <span className="text-gray-300">·</span>
                <span className="flex items-center gap-1 text-xs">
                  <i className="ri-gift-fill text-yellow-500 text-sm"></i>100% gratuito
                </span>
                <span className="text-gray-300">·</span>
                <span className="flex items-center gap-1 text-xs">
                  <i className="ri-checkbox-circle-fill text-yellow-500 text-sm"></i>Sem compromisso
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 animate-bounce hidden lg:block">
        <i className="ri-arrow-down-line text-2xl text-yellow-500/60"></i>
      </div>
    </section>
  );
}
